<template>
    <edit-resource
        v-if="form"
        :form="form"
        :title="title"
        always-submit
        buttons-always-active
        position="top"
        @close="$emit('close')"
        @submit="saveInterim"
    >

        <div class="w-768 space-y-24">
            <div class="flex items-center">
                <div class="flex-grow mr-48 space-y-24">
                    <div>
                        <h2 class="text-body font-semibold capitalize mb-12">
                            {{ $t('interims.form.information') }}
                        </h2>

                        <simple-input v-model="form.name">
                            {{ $t('interims.form.name') }}
                        </simple-input>
                    </div>

                    <div>
                        <h2 class="text-body font-semibold capitalize mb-12">
                            {{ $t('interims.office_type') }}
                        </h2>
                        <segmented-button
                            :id="$t('interims.office_type')"
                            v-model="form.type"
                            :options="[
                            {label: $t('interims.interim_office'), value: 'interim'},
                            {label: $t('interims.selection_office'), value: 'selection'},
                        ]"
                        />
                    </div>
                </div>

                <single-image-upload
                    v-model="form.logo_path_queued_id"
                    :image="interim ? interim.logo : null"
                    class="flex-none"
                    classes="w-192 h-192 rounded"
                    is-small
                    @clear="removeImage"
                ></single-image-upload>
            </div>

            <div>
                <div class="mb-12">
                    <h2 class="text-body font-semibold first-letter:uppercase">
                        {{ $t('interims.email') }}
                    </h2>
                    <p class="block mb-16" v-html="$t('interims.contact_person_info')"></p>
                </div>
                <div v-if="emailIsEditable || phoneIsEditable" class="flex">
                    <simple-input
                        class="w-1/2 mr-16"
                        :disabled="!emailIsEditable"
                        :input-label="$t('applications.email')"
                        v-model="form.contact_mail"
                    />
                    <simple-input
                        class="w-1/2 pl-16"
                        :disabled="!phoneIsEditable"
                        :input-label="$t('applications.phone')"
                        v-model="form.contact_phone"
                    />
                </div>
                <div v-else class="flex">
                    <div class="w-1/2">
                        <p class="mr-16 mb-4 text-body first-letter:uppercase font-semibold">{{ $t('interims.email') }}</p>
                        <p>{{ form.contact_mail }}</p>
                    </div>
                    <div class="w-1/2">
                        <p class="mr-16 mb-4 text-body first-letter:uppercase font-semibold">{{ $t('applications.phone') }}</p>
                        <p>{{ form.contact_phone }}</p>
                    </div>
                </div>
            </div>

            <loading v-if="fetchingTypes"/>
            <div v-else-if="types.length > 0">
                <h2 class="text-body font-semibold first-letter:uppercase mb-12">
                    {{ $t('interims.matrix') }}
                </h2>

                <div v-for="type in types" :key="type.id" class="flex items-center py-4 pl-12 pr-4 bg-white even:bg-primary-5">
                    <p class="w-1/2">
                        {{ type.name }}
                    </p>
                    <div class="w-1/2">
                        <simple-input
                            :static-id="type.name.toLowerCase().replace(' ','') + 'Input'"
                            v-model="typeValues[type.id]"
                        />
                    </div>
                </div>
            </div>
        </div>
    </edit-resource>
</template>

<script>
import PopupModal from './PopupModal';
import EditResource from './EditResource';
import SimpleInput from '../forms/Input';
import SingleImageUpload from '../forms/SingleImageUpload';
import Form from '../../etc/forms/Form';
import Interim from '../../models/Interim';
import * as interims from '../../services/InterimService';
import {normalizeJson} from '../../../shared/helpers';
import InterimMatrixType from '../../models/InterimMatrixType';
import * as types from '../../services/InterimMatrixTypeService';
import * as typeValues from '../../services/InterimMatrixTypeValueService';
import Loading from './Loading';
import RadioButton from '../forms/RadioButton';
import InterimMatrixTypeValue from '../../models/InterimMatrixTypeValue';
import SegmentedButton from "./SegmentedButton.vue";
import {useRepo} from 'pinia-orm';

export default {
    components: {SegmentedButton, RadioButton, Loading, SingleImageUpload, SimpleInput, EditResource, PopupModal},
    props: {
        interim: Interim,
        section: null,
        activeItem: {
            required: false,
            default: 'interim',
        },
    },
    data() {
        return {
            fetchingTypes: false,

            form: null,
            typeValues: {},
        };
    },
    computed: {
        title() {
            return this.$t(this.interim ? 'interims.form.title_edit' : 'interims.form.title');
        },
        types() {
            return useRepo(InterimMatrixType).where('type', this.form.data.type).orderBy('order').get();
        },
        emailIsEditable() {
            return !this.interim || this.interim && !this.interim.linked
        },
        phoneIsEditable() {
            return !this.interim || this.interim && !this.interim.contact_phone
        }
    },
    methods: {
        generateForm() {
            const fields = [
                {name: 'name'},
                {name: 'logo_path_queued_id'},
                {name: 'contact_mail'},
                {name: 'contact_phone'},
                {name: 'remove_image', value: false},
                {name: 'types', value: {}},
                {name: 'section_id', value: null},
                {name: 'type', value: this.activeItem},
            ];

            if (this.interim) {
                fields.forEach((field, index) => {
                    if (this.interim[field.name]) {
                        fields[index].value = JSON.parse(JSON.stringify(this.interim[field.name]));
                    }
                });

                let typeValues = {};
                this.interim.matrixValues.forEach(value => {
                    typeValues[value.type.id.toString()] = value.value;
                });
                this.typeValues = typeValues;
            }

            return new Form(fields);
        },
        fetch() {
            this.fetchingTypes = true;

            let requests = [];

            requests.push(types.index()
                .then(result => {
                    return useRepo(InterimMatrixType).save(normalizeJson(result.data));
                }));


            Promise.all(requests).then(() => {
                this.fetchingTypes = false;
            });
        },
        removeImage() {
            this.form.remove_image = true;
        },

        saveInterim() {
            if (this.interim) {
                return this.updateInterim();
            } else {
                return this.createInterim();
            }
        },
        createInterim() {
            let types = [];
            for (let typeId in this.typeValues) {
                if (this.typeValues.hasOwnProperty(typeId)) {
                    types.push({
                        type_id: typeId,
                        value: this.typeValues[typeId],
                    });
                }
            }

            if (this.section) {
                this.form.data.section_id = this.section.id;
            }

            return this.form.submit(data => {
                return interims.store({
                    data: {
                        attributes: data,
                        relationships: {
                            matrixValues: types,
                        },
                    },
                });
            }).then(result => {
                return useRepo(Interim).save(normalizeJson(result));
            }).then(inserts => {
                this.$emit('close');
            });
        },
        updateInterim() {
            let types = [];
            let types_destroy = [];

            for (let typeId in this.typeValues) {
                if (this.typeValues.hasOwnProperty(typeId) && this.types.find(x => x.id == typeId)) {
                    //push or update values if you don't change active type
                    types.push({
                        type_id: typeId,
                        value: this.typeValues[typeId],
                    });
                } else {
                    //Put values to remove in different array
                    types_destroy.push(this.interim.matrixValues.find(x => x.type_id == typeId).id);
                }
            }

            if (types_destroy.length) {
                //Loop the old values and destroy them
                for (let i = 0; i < types_destroy.length; i++) {
                    typeValues.destroy(types_destroy[i]);

                    useRepo(InterimMatrixTypeValue).destroy(types_destroy[i]);
                }
            }

            return this.form.submit(data => {
                return interims.update(this.interim.id, {
                    data: {
                        attributes: data,
                        relationships: {
                            matrixValues: types,
                        },
                    },
                });
            }).then(result => {
                return useRepo(Interim).save(normalizeJson(result));
            }).then(inserts => {
                this.$emit('close');
            });
        },
    },
    mounted() {
        this.form = this.generateForm();
        this.fetch();
    },
};
</script>
